
import { TW_AppBlogArticle, TW_AppBlogArticleImage, TW_AppBlogParagraphMargin } from "@utils/twClasses";
import gestionUtilisateur from '@img/Gestion_utilisateur.png';
import pageGestionUtilisateur from '@img/Page_gestion_utilisateur.png';
import gestionMembresEspaceTravail2 from '@img/Gestion_membres_espace_travail_2.png';
import selectionRole from '@img/Selection_role.png';
import gestionRoles from '@img/Gestion_roles.png';
import accesDroitsVues from '@img/Acces_droits_vues.png';
import gestionDroitsVues from '@img/gestion_droits_vues.png';

export default function Page() {

    return (
        <div className={TW_AppBlogArticle}>
            <h1 className="mb-6 text-3xl font-semibold text-black">
                Access rights
            </h1>
            <p className={TW_AppBlogParagraphMargin}>
                We have developed Compozz with the aim of creating a tool that can be used by all collaborators within your company. This necessarily implies advanced user and access rights management for workspaces, as not everyone can have access to all data.
            </p>
            <p className={TW_AppBlogParagraphMargin + " font-semibold + text-lg"}>
                Creating a user
            </p>
            <p className={TW_AppBlogParagraphMargin}>
                To create a user, you must be defined as Account Administrator, otherwise this operation will not be permitted. Click on your initial in the top right, then on "User management".
            </p>
            <img src={gestionUtilisateur} alt="Access to user management" width={346} height={329} className={TW_AppBlogArticleImage} />
            <p className={TW_AppBlogParagraphMargin}>
                Then click on "Add a user" and fill in the fields.
            </p>
            <p className={TW_AppBlogParagraphMargin}>
                Once the user is created, you can choose to define them as an account administrator as well; they will then also be able to manage users.
            </p>
            <p className={TW_AppBlogParagraphMargin}>
                You also have the option to deactivate a user; they will then no longer count towards the number of authorized users for the plan you have chosen.
            </p>
            <img src={pageGestionUtilisateur} alt="User management page" width={1918} height={914} className={TW_AppBlogArticleImage} />
            <p className={TW_AppBlogParagraphMargin + " font-semibold + text-lg"}>
                Managing rights
            </p>
            <p className={TW_AppBlogParagraphMargin}>
                You can then manage user rights for each workspace. In a workspace, go to the "members" tab at the bottom left of your screen. The list of users who already have a role appears. To access the complete list of users, click on "All users".
                <br />
                <br />
                Account administrators have full rights on all workspaces by default. For others, they have none by default. You will need to create roles and assign them to each user.
                <br />
                <br />
                A user who does not have a role assigned is a user who does not have access to the workspace. They won't even see it on their home page.
            </p>
            <img src={gestionMembresEspaceTravail2} alt="Page managing all members of a workspace" width={1906} height={915} className={TW_AppBlogArticleImage} />
            <p className={TW_AppBlogParagraphMargin}>
                Click on "Role management", then on "Add a role", fill in the field and then select it in the dropdown list "Select a role".
            </p>
            <img src={selectionRole} alt="Selecting a role to modify access rights" width={1913} height={402} className={TW_AppBlogArticleImage} />
            <div className="flex gap-4 border-1 rounded-lg p-3 border-compozz-purple mt-10 mb-10 bg-white/20 ">
                <div className="">
                    <p className="mb-5 font-semibold text-compozz-purple">
                        Good to know!
                    </p>
                    <p>
                        Before going any further, it is important to understand that there are two levels of rights: rights on the data model and rights on the data.
                        Rights on the data model are the rights that allow you to create, modify, delete objects, views, or dashboards, for example. This is about the definition of the information you will manage in Compozz.
                        Rights on the data are the rights that allow you to read, edit, delete records (rows) in an object, a view, or a dashboard, for example. This is about the information itself.
                        <br />
                        <br />
                        In the rest of this page, when we talk about objects, views, or dashboards, it is about the data model, while when we talk about records or rows, it is about the data.
                    </p>
                </div>
            </div>
            <p className={TW_AppBlogParagraphMargin}>
                In the first category "Allow access", you can define rights on objects in general:
            </p>
            <ul className={TW_AppBlogParagraphMargin}>
                <li>
                    <span className="font-semibold">Full access:</span> the user has all rights on objects (configuration included) and on records.
                </li>
                <li>
                    <span className="font-semibold">Edit access:</span> the user can edit records but not the configuration of the objects.
                </li>
                <li>
                    <span className="font-semibold">Read access:</span> the user has access to created objects but cannot create new ones, modify the configuration, or edit the data. They can only read records.
                </li>
                <li>
                    <span className="font-semibold">Access denied:</span> the user does not have access to the object; it does not appear in their session
                </li>
            </ul>
            <p className={TW_AppBlogParagraphMargin}>
                Below the access level, the "Options…" menu grants extra rights on records, summarised by chips:
            </p>
            <ul className={TW_AppBlogParagraphMargin}>
                <li>
                    <span className="font-semibold">Add records</span> and <span className="font-semibold">Delete records:</span> available from the edit access. They are checked by default when you switch a right to edit or full access, and you can uncheck them.
                </li>
                <li>
                    <span className="font-semibold">Export data:</span> available from the read access. It is never checked by default: exporting the records of an object stays reserved to the account owners until an administrator explicitly grants it.
                </li>
            </ul>
            <p className={TW_AppBlogParagraphMargin}>
                The same applies to access rights for dashboards, except for edit access which does not exist in this case.
            </p>
            <p className={TW_AppBlogParagraphMargin}>
                For rights on members, there are only two possibilities: either the user has the right to modify the member list of the workspace, create and modify roles, or they do not.
            </p>
            <p className={TW_AppBlogParagraphMargin}>
                The same applies to views: either the user can create views, modify them, delete them, and share them, or they cannot. If the user does not have rights on views, they will only be able to work with views that have been shared with them.
            </p>
            <p className={TW_AppBlogParagraphMargin}>
                In the second category "Rights on objects", you can more precisely define a user's rights on each object. This way, you can give full access to objects to a user, but restrict access to one or more specific objects. Or conversely, you can deny access to all objects and then authorize access only to certain objects.
            </p>
            <div className="flex gap-4 border-1 rounded-lg p-3 border-compozz-purple mt-10 mb-10 bg-white/20 ">
                <div className="">
                    <p className="mb-5 font-semibold text-compozz-purple">
                        Good to know!
                    </p>
                    <p>
                        Managing rights on each object takes precedence over managing rights on all objects. This means that if you give full access to an object to a user while you have denied them access to objects, they will be able to see that object and modify it as they see fit.
                    </p>
                </div>
            </div>
            <img src={gestionRoles} alt="View of rights granted to the Collaborator role" width={1910} height={913} className={TW_AppBlogArticleImage} />
            <div className="flex gap-4 border-1 rounded-lg p-3 border-compozz-purple mt-10 mb-10 bg-white/20 ">
                <div className="">
                    <p className="mb-5 font-semibold text-compozz-purple">
                        Good to know!
                    </p>
                    <p>
                        It is also possible to uncheck an access right. This allows you to easily disable an access right (for testing, for example) and reactivate it later.
                        In the case of access rights on all objects, dashboards, members, and views, this is equivalent to configuring the access right to "Access denied".
                        In the case of access rights on each object, this is equivalent to restoring the default value, i.e., applying the access right defined on all objects.
                    </p>
                </div>
            </div>
            <p className={TW_AppBlogParagraphMargin + " font-semibold + text-lg"}>
                Managing rights on views
            </p>
            <p className={TW_AppBlogParagraphMargin}>
                When a user creates a view, this view is private by default. This means that only the creator of the view can see and modify it.
                But the user can share the view with other users by defining access rights on the view.
            </p>
            <p className={TW_AppBlogParagraphMargin}>
                When you hover over a view, 3 dots appear on the right; click on them, then on "Share view".
            </p>
            <img src={accesDroitsVues} alt="Access to the popup for managing rights for views" width={919} height={332} className={TW_AppBlogArticleImage} />
            <p className={TW_AppBlogParagraphMargin}>
                Select a role, then the access you want to grant:
            </p>
            <ul className={TW_AppBlogParagraphMargin}>
                <li>
                    <span className="font-semibold">Full access:</span> users of the role have all rights on the view and on the data, including creating and deleting records.
                </li>
                <li>
                    <span className="font-semibold">Edit access:</span> users of the role do not have access to the view configuration but can create, modify, and delete records.
                </li>
                <li>
                    <span className="font-semibold">Read access:</span> users of the role do not have access to the view configuration but can read the data.
                </li>
                <li>
                    <span className="font-semibold">Access denied:</span> users of the role do not have access to the view.
                </li>
            </ul>
            <img src={gestionDroitsVues} alt="Popup for managing rights for views" width={745} height={433} className={TW_AppBlogArticleImage} />
            <p className={TW_AppBlogParagraphMargin}>
                If you do not define any rights, this view will remain private and will only be accessible by you.
            </p>
            <p className={TW_AppBlogParagraphMargin}>
                You have the keys in hand to precisely configure your collaborators' access rights. If questions remain, don't hesitate to <a href="/freeTrial" className="text-compozz-darker-purple hover:underline">contact us</a>!
            </p>
        </div>
    )
}